import { useParams, Link } from 'react-router-dom';
import { getArticleById, articles } from '@/mocks/articles';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOMeta, { generateArticleSchema, generateBreadcrumbSchema } from '@/components/feature/SEOMeta';
import ArticleCard from '@/components/base/ArticleCard';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(id || '');
  const relatedArticles = articles
    .filter((a) => a.category === article?.category && a.id !== article?.id)
    .slice(0, 3);

  if (!article) {
    return (
      <main className="max-w-[1400px] mx-auto px-4 md:px-6 py-20 text-center">
        <div className="w-20 h-20 bg-background-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
        </div>
        <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-2">文章未找到</h1>
        <p className="text-foreground-500 mb-6">您访问的文章不存在或已被移除</p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-white rounded-full font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i>
          返回首页
        </Link>
      </main>
    );
  }

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: article.categoryName, href: `/category/${article.category}` },
    { label: article.title },
  ];

  const articleSchema = generateArticleSchema(article);
  const breadcrumbSchema = generateBreadcrumbSchema(breadcrumbItems);

  return (
    <>
      <SEOMeta type="article" data={article as unknown as Record<string, unknown>} />
      <SEOMeta type="breadcrumb" data={breadcrumbItems as unknown as Record<string, unknown>} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <title>{article.title} - 乐球体育</title>
      <meta name="description" content={article.excerpt} />
      <meta name="keywords" content={article.tags.join(',')} />
      <meta property="og:title" content={article.title} />
      <meta property="og:description" content={article.excerpt} />
      <meta property="og:image" content={article.coverImage} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={`https://www.leqiusports.com/article/${article.id}`} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={article.title} />
      <meta name="twitter:description" content={article.excerpt} />
      <meta name="twitter:image" content={article.coverImage} />
      <link rel="canonical" href={`https://www.leqiusports.com/article/${article.id}`} />

      <main>
        <article className="max-w-[900px] mx-auto px-4 md:px-6 py-6">
          <Breadcrumb items={breadcrumbItems} />

          <header className="mb-8">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <Link
                to={`/category/${article.category}`}
                className="text-xs text-primary-500 font-medium bg-primary-50 px-3 py-1 rounded-full hover:bg-primary-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                {article.categoryName}
              </Link>
              {article.tags.map((tag) => (
                <span key={tag} className="text-xs text-foreground-500 bg-background-100 px-3 py-1 rounded-full">
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>

            <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-primary-100 rounded-full flex items-center justify-center">
                  <i className="ri-user-line text-primary-500 text-xs"></i>
                </div>
                <span>{article.author}</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-calendar-line text-foreground-400"></i>
                <span>{article.publishDate}</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-eye-line text-foreground-400"></i>
                <span>{article.viewCount > 10000 ? `${(article.viewCount / 10000).toFixed(1)}万次阅读` : `${article.viewCount}次阅读`}</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-chat-3-line text-foreground-400"></i>
                <span>{article.commentCount}条评论</span>
              </div>
            </div>
          </header>

          <div className="mb-8 rounded-xl overflow-hidden">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-auto object-cover"
            />
          </div>

          <div
            className="prose prose-lg max-w-none prose-headings:font-heading prose-headings:text-foreground-950 prose-p:text-foreground-700 prose-p:leading-relaxed prose-h2:text-xl prose-h2:font-bold prose-h2:mt-8 prose-h2:mb-4 prose-p:mb-4 article-content"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-10 pt-6 border-t border-background-200/70">
            <div className="flex items-center gap-4 flex-wrap">
              <span className="text-sm text-foreground-500">分享：</span>
              <button className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center hover:bg-primary-100 hover:text-primary-500 transition-colors cursor-pointer" aria-label="分享到微博">
                <i className="ri-weibo-line"></i>
              </button>
              <button className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center hover:bg-primary-100 hover:text-primary-500 transition-colors cursor-pointer" aria-label="分享到微信">
                <i className="ri-wechat-line"></i>
              </button>
              <button className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center hover:bg-primary-100 hover:text-primary-500 transition-colors cursor-pointer" aria-label="复制链接">
                <i className="ri-link"></i>
              </button>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-[900px] mx-auto">
              <h2 className="font-heading text-xl font-bold text-foreground-950 mb-6">相关推荐</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map((rel) => (
                  <ArticleCard key={rel.id} article={rel} variant="default" />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </>
  );
}